import React, { useState } from 'react';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import type { EntregaRequest } from '../../../store/MSCommerce/Pedidos/types';
import styles from '../Ofertas/Ofertas.module.scss';

interface EntregaModalProps {
  onClose: () => void;
  onSubmit: (entrega: EntregaRequest) => void;
}

const TIPOS = [
  { value: 'PUNTO_ENTREGA', label: 'Punto de entrega' },
  { value: 'DOMICILIO', label: 'Envío a domicilio' },
  { value: 'NACIONAL', label: 'Envío nacional' },
];

export const EntregaModal: React.FC<EntregaModalProps> = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({
    tipo: 'PUNTO_ENTREGA',
    contactoNombre: '',
    contactoPhone: '',
    direccion: '',
    referencia: '',
    ciudad: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const field = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const requiereDireccion = form.tipo !== 'PUNTO_ENTREGA';

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.contactoNombre.trim()) e.contactoNombre = 'Requerido';
    if (!form.contactoPhone.trim()) e.contactoPhone = 'Requerido';
    if (requiereDireccion) {
      if (!form.direccion.trim()) e.direccion = 'Requerida';
      if (!form.ciudad.trim()) e.ciudad = 'Requerida';
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    onSubmit({
      tipo: form.tipo,
      contactoNombre: form.contactoNombre,
      contactoPhone: form.contactoPhone,
      direccion: requiereDireccion ? form.direccion : null,
      referencia: requiereDireccion ? form.referencia || null : null,
      ciudad: requiereDireccion ? form.ciudad : null,
      costoEnvio: null,
    });
  };

  return (
    <div className={styles.modal__overlay} onClick={onClose}>
      <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>

        <div className={styles.modal__header}>
          <div className={styles.modal__headerLeft}>
            <h2 className={styles.modal__title}>¿Cómo quieres recibirlo?</h2>
          </div>
          <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
        </div>

        <div className={styles.modal__body}>
          <div className={styles.modal__grid}>

            <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
              <label className={styles.modal__label}>Método de entrega *</label>
              <div className={styles.modal__statusGroup}>
                {TIPOS.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    className={`${styles.modal__statusBtn} ${form.tipo === t.value ? styles['modal__statusBtn--active'] : ''}`}
                    onClick={() => field('tipo', t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {form.tipo === 'PUNTO_ENTREGA' && (
                <p style={{ fontSize: 12, color: '#7D6A61', marginTop: 8 }}>
                  Retiro los sábados en C.C. Quicentro Norte (10:00 am) o C.C. El Recreo (11:00 am y 4:00 pm).
                </p>
              )}
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>Nombre de contacto *</label>
              <input
                className={`${styles.modal__input} ${errors.contactoNombre ? styles['modal__input--error'] : ''}`}
                value={form.contactoNombre}
                onChange={e => field('contactoNombre', e.target.value)}
              />
              {errors.contactoNombre && <span className={styles.modal__error}>{errors.contactoNombre}</span>}
            </div>

            <div className={styles.modal__field}>
              <label className={styles.modal__label}>WhatsApp de contacto *</label>
              <input
                className={`${styles.modal__input} ${errors.contactoPhone ? styles['modal__input--error'] : ''}`}
                placeholder="0999999999"
                value={form.contactoPhone}
                onChange={e => field('contactoPhone', e.target.value)}
              />
              {errors.contactoPhone && <span className={styles.modal__error}>{errors.contactoPhone}</span>}
            </div>

            {requiereDireccion && (
              <>
                <div className={`${styles.modal__field} ${styles['modal__field--full']}`}>
                  <label className={styles.modal__label}>Dirección *</label>
                  <input
                    className={`${styles.modal__input} ${errors.direccion ? styles['modal__input--error'] : ''}`}
                    value={form.direccion}
                    onChange={e => field('direccion', e.target.value)}
                  />
                  {errors.direccion && <span className={styles.modal__error}>{errors.direccion}</span>}
                </div>

                <div className={styles.modal__field}>
                  <label className={styles.modal__label}>Ciudad *</label>
                  <input
                    className={`${styles.modal__input} ${errors.ciudad ? styles['modal__input--error'] : ''}`}
                    value={form.ciudad}
                    onChange={e => field('ciudad', e.target.value)}
                  />
                  {errors.ciudad && <span className={styles.modal__error}>{errors.ciudad}</span>}
                </div>

                <div className={styles.modal__field}>
                  <label className={styles.modal__label}>Referencia</label>
                  <input
                    className={styles.modal__input}
                    placeholder="Casa, edificio, punto cercano..."
                    value={form.referencia}
                    onChange={e => field('referencia', e.target.value)}
                  />
                </div>
              </>
            )}

          </div>
        </div>

        <div className={styles.modal__footer}>
          <button className={styles.modal__cancel} onClick={onClose}>Cancelar</button>
          <button className={styles.modal__submit} onClick={handleSubmit}>
            <CheckOutlined /> Continuar
          </button>
        </div>

      </div>
    </div>
  );
};
